import type { ReactNode } from 'react';
import React, { useState } from 'react';

import { Select } from '@aline/common';
import { LinearProgress, Typography } from '@mui/material';
import { AlineAlert, AlineButton, SmallCloseIconButton } from 'src/components';
import type { AlineAlertProps } from 'src/components';
import {
  getIsWalkthroughCtaDisabled,
  useFinishFieldActions,
  useGetStartWalkthrough,
  useRequesterHasWalkthroughActionsToComplete,
  useWalkthrough,
  useWalkthroughActive,
} from 'src/editor/toolbars';
import type { RequesterFieldCounts } from 'src/editor/toolbars';
import { usePageWidthPx } from 'src/editor/vars';
import { Apollo } from 'src/gql/generated';
import { useMaybeWorkflowParams } from 'src/helpers';
import {
  useDocumentSignatureStatus,
  useExportDocumentsToPdf,
  useGetEditorUserState,
  useGetWorkflowFiles,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import type { EditorAlertProps } from './types';

const useFieldProgressStyles = makeStyles()(theme => ({
  fieldProgress: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    gap: theme.spacing(2),
  },
  progress: {
    flexGrow: 1,
    height: 6,
  },
}));

export function FieldProgress({
  requesterFieldCounts,
}: {
  requesterFieldCounts: RequesterFieldCounts;
}): JSX.Element {
  const { classes } = useFieldProgressStyles();

  return (
    <div className={classes.fieldProgress}>
      <Typography noWrap variant="body1">
        {requesterFieldCounts.completed}/{requesterFieldCounts.total} fields completed
      </Typography>
      <LinearProgress
        className={classes.progress}
        value={(requesterFieldCounts.completed / requesterFieldCounts.total) * 100}
        variant="determinate"
      />
    </div>
  );
}

const useStyles = makeStyles<{ bannerWidth: number }>()((theme, { bannerWidth }) => ({
  banner: {
    bottom: theme.spacing(2),
    boxSizing: 'border-box',
    left: `calc(50% - ${bannerWidth / 2}px)`,
    position: 'absolute',
    width: bannerWidth,
    zIndex: theme.zIndex.appBar,
    [theme.breakpoints.down('md')]: {
      bottom: theme.spacing(2),
      left: theme.spacing(1),
      position: 'fixed',
      right: theme.spacing(1),
      width: 'auto',
    },
  },
  buttonOnly: {
    bottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    left: '50%',
    position: 'absolute',
    transform: 'translateX(-50%)',
    zIndex: theme.zIndex.appBar,
  },
  downloadText: {
    color: theme.palette.common.white,
    fontSize: 14,
  },
  finishSigningButton: {
    height: 'unset',
  },
  message: {
    flexGrow: 1,
  },
  signedText: {
    color: theme.palette.common.white,
    marginRight: theme.spacing(2),
  },
}));

interface ContractAlertInfo {
  buttonText: string;
  dismissible: boolean;
  message: ReactNode | string;
  onClick: (e: React.MouseEvent) => void;
  severity: AlineAlertProps['severity'];
  submitting: boolean;
  buttonDataCy?: string;
  disabled?: boolean;
  isFinishSigning?: boolean;
}

function useContractAlert(): ContractAlertInfo | null {
  const { walkThroughActive } = useWalkthroughActive();
  const { requesterHasWalkthroughActionsToComplete } =
    useRequesterHasWalkthroughActionsToComplete();
  const { userDeclinedSigning } = useGetEditorUserState();
  const { startWalkthrough } = useGetStartWalkthrough();
  const { documentId, workflowId } = useMaybeWorkflowParams();
  const { isSigned, signedAt } = useDocumentSignatureStatus(documentId);
  const { workflowFiles } = useGetWorkflowFiles(workflowId ?? null);
  const {
    fieldActionsPendingSave,
    finishFieldActions,
    finishingFieldActions,
    requesterNeedsToClickFinish,
  } = useFinishFieldActions();
  const {
    advanceWalkthrough,
    requesterFieldCounts,
    submitting: walkthroughSubmitting,
  } = useWalkthrough();
  // Uniq documentIds since we're not sure which route we will be at (document route vs. workflow route in editor).
  const documentIdSet = new Set([
    ...workflowFiles.map(workflowFile => workflowFile.document.id),
    documentId,
  ]);
  const { exportToPdf, loading: exportingPDfs } = useExportDocumentsToPdf([...documentIdSet]);

  if (isSigned)
    return {
      buttonText: workflowId ? 'Download Signed Documents' : 'Download Signed Document',
      dismissible: false,
      message: signedAt ? `Document signed on ${signedAt}` : 'Document signed',
      onClick: exportToPdf,
      severity: 'success',
      submitting: exportingPDfs,
    };

  if (!walkThroughActive && requesterHasWalkthroughActionsToComplete && !userDeclinedSigning)
    return {
      buttonDataCy: Select.document.editor.walkthrough.startWalkthroughButton,
      buttonText: 'Start',
      dismissible: true,
      message: 'It looks like you have actions to complete.  Click start to begin.',
      onClick: startWalkthrough,
      severity: 'info',
      submitting: false,
    };

  if (walkThroughActive && requesterHasWalkthroughActionsToComplete && !userDeclinedSigning)
    return {
      buttonText: requesterNeedsToClickFinish ? 'Finish Signing' : 'Next',
      disabled: getIsWalkthroughCtaDisabled({
        fieldActionsPendingSave,
        requesterNeedsToClickFinish,
      }),
      dismissible: false,
      isFinishSigning: requesterNeedsToClickFinish,
      message: <FieldProgress requesterFieldCounts={requesterFieldCounts} />,
      onClick: async e => {
        if (requesterNeedsToClickFinish) return finishFieldActions();

        await advanceWalkthrough(e, { canExecuteFinish: true });
      },
      severity: 'info',
      submitting: finishingFieldActions || walkthroughSubmitting,
    };

  return null;
}

export function ContractAlert({ document }: EditorAlertProps): JSX.Element | null {
  const editorAlertInfo = useContractAlert();
  const { pageWidthPx } = usePageWidthPx();
  const [dismissAlert, setDismissAlert] = useState<boolean>(false);
  const isTemplate = document.variant === Apollo.DocumentVariant.Template;
  const { classes } = useStyles({ bannerWidth: Math.max(640, pageWidthPx + 100) });

  if (!pageWidthPx || !editorAlertInfo || dismissAlert || isTemplate) return null;

  if (editorAlertInfo.isFinishSigning) {
    return (
      <div className={classes.buttonOnly} data-cy={Select.document.alert.contractAlert}>
        <AlineButton
          buttonClassName={classes.finishSigningButton}
          color="primary"
          data-cy={editorAlertInfo.buttonDataCy}
          disabled={editorAlertInfo.disabled}
          loading={editorAlertInfo.submitting}
          size="large"
          variant="contained"
          onClick={editorAlertInfo.onClick}
        >
          <Typography color="inherit" variant="subtitle1">
            {editorAlertInfo.buttonText}
          </Typography>
        </AlineButton>
      </div>
    );
  }

  return (
    <div className={classes.banner} color="secondary" data-cy={Select.document.alert.contractAlert}>
      <AlineAlert
        action={
          <>
            <AlineButton
              color="inherit"
              data-cy={editorAlertInfo.buttonDataCy}
              disabled={editorAlertInfo.disabled}
              loading={editorAlertInfo.submitting}
              onClick={editorAlertInfo.onClick}
            >
              {editorAlertInfo.buttonText}
            </AlineButton>
            {editorAlertInfo.dismissible && (
              <SmallCloseIconButton onClick={() => setDismissAlert(true)} />
            )}
          </>
        }
        classes={{
          message: classes.message,
        }}
        severity={editorAlertInfo.severity}
      >
        {editorAlertInfo.message}
      </AlineAlert>
    </div>
  );
}
